import { reportQuerySchema, type ReportQuery } from '@chukua/common';
import { ShieldAlert } from 'lucide-react';
import { useCallback } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { useReportExport } from '../hooks/use-report-export';
import { useReportTable } from '../hooks/use-report-table';
import { getFigures, type ReportSummary } from '../reports.api';
import { CLEARED_FILTERS, isReportFiltered } from '../policies/visible-filters.policy';
import { ExportButton } from './ExportButton';
import { RefreshedStamp } from './RefreshedStamp';
import { ReportFilterBar } from './ReportFilterBar';
import { ReportFiguresTable } from './ReportFiguresTable';
import { ReportTrendSection } from './ReportTrendSection';

/** A report's figures tab: filters, the trend (when the report has one), the figures, and the export of them. */
export function ReportFiguresView({ report }: { report: ReportSummary }) {
  const load = useCallback((query: ReportQuery) => getFigures(report.key, query), [report.key]);
  const { state, list, isRefreshing } = useReportTable(reportQuerySchema, load);
  const exporter = useReportExport(report, 'figures');

  if (state.status === 'error') throw state.error;
  if (state.status === 'forbidden') return <EmptyState icon={ShieldAlert} title="Not allowed" description="You don't have access to this report." />;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <RefreshedStamp report={report} />
        <ExportButton isExporting={exporter.isExporting} error={exporter.error} onExport={() => void exporter.start(list.query)} />
      </div>
      <ReportFilterBar
        declared={report.filters}
        values={list.query}
        onChange={(patch) => list.setQuery(patch)}
        onClear={() => list.setQuery(CLEARED_FILTERS)}
        isFiltered={isReportFiltered(list.isFiltered, list.query)}
      />
      {report.hasTrend && <ReportTrendSection reportKey={report.key} query={list.query} />}
      {state.status === 'loading' ? (
        <TableSkeleton label="Loading figures" withSearch rows={6} />
      ) : (
        <ReportFiguresTable page={state.page} list={list} isRefreshing={isRefreshing} />
      )}
    </>
  );
}
